import React, { useCallback, useEffect, useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import cn from 'classnames';
import { CloseButton, Loader } from '../../index';
import { ScrollContext, lockUnlockPage } from '../utils';
import * as styles from './Modal.module.scss';

const getChildrenComponent = (component, children) => {
  if (component) return component;
  if (typeof children === 'function') return children;
  return () => children;
};

const useModalPageLock = (isOpen, setState) => {
  useEffect(() => {
    if (isOpen) {
      setState(prev => ({ ...prev, active: true, disable: false }));
      lockUnlockPage.lock();
    }
    return () => lockUnlockPage.unlock();
  }, [isOpen, setState]);
};

export default function Modal({
  isOpen,
  loader,
  closeModalCallback,
  animation = 'show',
  component,
  children,
  closeButton = true,
  containerMod,
  contentMod,
  theme,
  className,
}) {
  const [state, setState] = useState({ active: false, disable: true });
  const modalRef = useRef(null);

  useModalPageLock(isOpen, setState);

  const onClose = useCallback(() => {
    setState(prev => ({ ...prev, active: false }));
  }, []);

  const onAnimationEnd = useCallback(() => {
    !state.active && closeModalCallback();
  }, [state.active]);

  if (!isOpen) return null;

  const Children = getChildrenComponent(component, children);

  return ReactDOM.createPortal(
    <ScrollContext.Provider value={modalRef.current}>
      <section
        id="modal"
        ref={modalRef}
        className={cn(
          styles.popup,
          state.disable && styles.disable,
          theme && styles[theme],
          className,
        )}
        onClick={event => {
          if (event.target.classList.value !== modalRef.current.className) return;
          onClose();
        }}
      >
        {closeButton && <CloseButton className={styles.closeButton} onClose={onClose} />}
        {loader && <Loader />}
        <div
          className={cn(
            styles.container,
            state.active && styles.active,
            loader && styles.loading,
            animation && styles[animation],
            containerMod && styles[containerMod],
          )}
          onAnimationEnd={onAnimationEnd}
        >
          <div className={cn(styles.content, contentMod && styles[contentMod])}>
            {Children(onClose, onAnimationEnd)}
          </div>
        </div>
      </section>
    </ScrollContext.Provider>,
    document.getElementById('___gatsby'),
  );
}
